import Store from '@/store/index.js'
import {getViewsOfTree} from '@/api/FirebaseViewWrapper.js'
import { removeHtml } from '@/api/Utilities.js'
import { deleteCard } from '@/api/FirebaseBoardWrapper.js'

// v9 moduler 方式
import { firebaseApp } from '@/firebase.js'
import { getFirestore, doc, getDocs, getDoc, setDoc, addDoc, updateDoc, deleteDoc, limit, startAfter, orderBy, where, query, collection, serverTimestamp, runTransaction } from 'firebase/firestore'
import { getMyUserId, getMyUserData, getUserData } from '@/api/FirebaseUserWrapper'
import * as constant from '@/constant/constant.js'

const db = getFirestore(firebaseApp)
const DOMAIN_NAME = constant.DEFAULT_DOMAIN_NAME

/**
 * Tree関係
 */

/**
 * getTreeFromTreeId:
 *   Treeを取得する
 *
 * @param {string} treeId
 * @returns
 */
export async function getTreeFromTreeId (treeId) {
  const treeRef = doc(db, 'treesV2', treeId)
  // treeの取得
  try {
    const treeSnapshot = await getDoc(treeRef)
    if (treeSnapshot.exists()) {
      const treeId = {
        id: treeSnapshot.id
      }
      const treeData = { ...treeSnapshot.data(), ...treeId }
      return treeData
    } else {
      return false
    }
  } catch (err) {
    return false
  }
}
/**
 * getTrees:
 *   Treeの一覧を取得する
 *
 * indexOptions:
 *   targetUserId
 *   lastTreeValue
 *   categories
 *
 * indexName
 *   (Searchqnq.vue) 内のもの
 *     recommend (reputation)
 *       注目
 *       新着順
 *     team
 *       所属するteamのtreeを取得
 */
export async function getTrees (indexName, indexOptions) {
  if (indexName === 'recommend') {
    indexOptions.orderBy = 'updatedAt'
    return getTreesByIndexOptions(indexOptions)
  } else if (indexName === 'team') {
    indexOptions.orderBy = 'updatedAt'
    return getTreesByIndexOptions(indexOptions)
  }
}

/**
 * _joinTreeAndUser:
 *   treeの doc と treeId を使って
 *   tree の情報と treeOwner のユーザ情報を結合させる
 *
 * @param {object} treeDoc
 * @param {string} treeId
 * @returns
 */
async function _joinTreeAndUser (treeDoc, treeId) {
  if (location.hostname !== DOMAIN_NAME) { console.log(`_joinTreeAndUser(${JSON.stringify(treeDoc)}, ${treeId})`) }
  const treeOwnerData = await getUserData(treeDoc.userId)
  const views = await getViewsOfTree(treeId, treeDoc.userId)
  const userData = treeOwnerData || {name: 'unknown user'}

  const appendedData = {
    url: '/tree/' + treeId,
    id: treeId,
    userName: userData.name,
    views: views
  }
  const tree = { ...treeDoc, ...appendedData }

  if (location.hostname !== DOMAIN_NAME) { console.log(`tree: ${JSON.stringify(tree)}`) }
  return tree
}

async function getTreesByIndexOptions (indexOptions) {
  /**
   * indexOptions
   *   targetUserId: 未定義ならば全員
   *   targetTeamId: 未定義なら全チーム
   *   lastTreeValue: orderBy のパラメータの値
   *   orderBy: ソートするパラメータ
   *   treeStatus: solved でフィルタをかける 'solved' | 'unsolved' | 'all' (未定義は'all')
   *   limitNum: getするlimit数を指定。（未定義は5)
   */
  if (location.hostname !== DOMAIN_NAME) { console.log(`getTreesByIndexOptions(${JSON.stringify(indexOptions)})`) }
  const treeRef = collection(db, 'treesV2')
  let q = query(treeRef)

  /**
   * indexOptionsにtargetTeamがある場合は、特定のteamのみ抽出
   * searchqnqの時 or 他人のprofileでは自分のteamに適合するものを抽出する
   */
  if (indexOptions.targetTeamId) {
    q = query(q, where('scope', '==', indexOptions.targetTeamId))
  } else {
    const myUserData = await getMyUserData()
    const myTeams = myUserData.teams ? myUserData.teams : []
    q = query(q, where('scope', 'in', myTeams))
  }

  // targetUserIdのみ
  if (indexOptions.targetUserId) {
    q = query(q, where('userId', '==', indexOptions.targetUserId))
  }

  // solved でフィルタするかどうか
  if (indexOptions.treeStatus === 'unsolved') {
    q = query(q, where('solved', '==', false))
  } else if (indexOptions.treeStatus === 'solved') {
    q = query(q, where('solved', '==', true))
  }
  // 並べ替え
  q = query(q, orderBy(indexOptions.orderBy, 'desc'))

  if (indexOptions.lastTreeValue) {
    q = query(q, startAfter(indexOptions.lastTreeValue))
  }

  const limitNum = indexOptions.limitNum ? indexOptions.limitNum : 5
  q = query(q, limit(limitNum))

  const querySnapshot = await getDocs(q)

  const trees = await Promise.all(querySnapshot.docs.map(async doc => {
    return _joinTreeAndUser(doc.data(), doc.id)
  }))
  const lastTreeValue = trees.length > 0 ? trees.slice(-1)[0][indexOptions.orderBy] : null

  return [trees, lastTreeValue]
}

export async function addTree (issue, maxBranchNum, team) {
  const userId = await getMyUserId()
  // add tree to db.
  const scope = team
  let treeData = {
    categories: [],
    createdAt: serverTimestamp(),
    imageUrl: '',
    issue: issue,
    maxBranchNum: maxBranchNum,
    reputation: 1000,
    scope: scope,
    solved: false,
    totalQuestions: 1,
    updatedAt: serverTimestamp(),
    userId: userId,
    isDeleted: false
  }
  const treeRef = collection(db, 'treesV2')
  const treePath = await addDoc(treeRef, treeData)
  // add node to db.
  const nodeData = {
    childsNum: 0,
    createdAt: serverTimestamp(),
    marker: 'root',
    number: 0,
    parentId: '',
    question: issue,
    userId: userId,
    updatedAt: serverTimestamp(),
    mentionedUsers: [],
    likesNum: 0,
    cloneCardId: null,
    reference: null,
    order: Date.now()
  }
  const nodeRef = collection(db, treePath.path + '/nodes')
  await addDoc(nodeRef, nodeData)

  return treePath
}

export async function deleteTree (treeId) {
  const treeData = {
    updatedAt: serverTimestamp(),
    isDeleted: true
  }
  const docRef = doc(db, 'treesV2', treeId)
  try {
    const treeRef = await updateDoc(docRef, treeData)
    return treeRef
  } catch (err) {
    console.error(err)
    throw err
  }
}

export async function updateTree (treeId, treeData) {
  treeData.updatedAt = serverTimestamp()
  const docRef = doc(db, 'treesV2', treeId)
  try {
    const treeRef = await updateDoc(docRef, treeData)
    return treeRef
  } catch (err) {
    console.error(err)
    throw err
  }
}

export async function addNode (treeId, question, parentId, marker, mentionedUsers, reference) {
  if (!parentId) {
    console.error('add root')
    return
  }
  const userId = await getMyUserId()
  // update parent node's childsNum.
  const parentNodeDocRef = doc(db, 'treesV2/' + treeId + '/nodes', parentId)
  try {
    await runTransaction(db, async (transaction) => {
      const parentNode = await transaction.get(parentNodeDocRef)
      const childsNum = parentNode.data().childsNum + 1
      transaction.update(parentNodeDocRef, { childsNum: childsNum })
    })
  } catch (err) {
    console.error(err)
    return null
  }

  const treeDocRef = doc(db, 'treesV2', treeId)
  try {
    const totalQuestions = await runTransaction(db, async (transaction) => {
      const tree = await transaction.get(treeDocRef)
      const totalQuestions = tree.data().totalQuestions + 1
      transaction.update(treeDocRef, { totalQuestions: totalQuestions, updatedAt: serverTimestamp() })
      return totalQuestions
    })

    // add node to db.
    const nodeData = {
      childsNum: 0,
      createdAt: serverTimestamp(),
      marker: marker,
      number: totalQuestions - 1,
      parentId: parentId,
      question: question,
      userId: userId,
      updatedAt: serverTimestamp(),
      mentionedUsers: mentionedUsers,
      likesNum: 0,
      cloneCardId: null,
      reference: reference,
      order: Date.now()
    }
    const nodeRef = collection(db, 'treesV2/' + treeId + '/nodes')
    const nodePath = await addDoc(nodeRef, nodeData)
    return nodePath
  } catch (err) {
    console.error(err)
    return null
  }
}

/**
 * addNodesbyCard:
 *   Cardの数だけNodeを追加する
 *
 * @param {*} treeId
 * @param {*} nodeId
 * @param {*} cards
 */
export async function addNodesbyCard (treeId, nodeId, teamId, cards) {
  for await (const card of cards) {
    // html関連削除
    const message = removeHtml(card.question)
    // 参照元の作成
    const reference = {
      treeId: card.treeId,
      nodeNumber: card.nodeNumber,
      cardId: card.id
    }
    // nodeの追加
    try {
      // nodeの追加
      await addNode(treeId, message, nodeId, card.marker, [], reference)
      // boardからcardを削除
      await deleteCard(card.id, teamId)
      // コピー元treeのclipを削除
      if (treeId && nodeId) {
        await updateCloneCard(treeId, nodeId, null)
      }
    } catch (err) {
      console.log(err)
    }
  }
}

/**
 * addNodebyCard:
 *   Cardの数だけNodeを追加する
 *
 * @param {*} treeId
 * @param {*} nodeId
 * @param {*} card
 */
export async function addNodebyCard (treeId, nodeId, teamId, card) {
  // html関連削除
  const message = removeHtml(card.question)
  // 参照元の作成
  const reference = {
    treeId: card.treeId,
    nodeNumber: card.nodeNumber,
    cardId: card.id
  }
  // nodeの追加
  try {
    // nodeの追加
    await addNode(treeId, message, nodeId, card.marker, [], reference)
    // boardからcardを削除
    await deleteCard(card.id, teamId)
    // コピー元treeのclipを削除
    if (treeId && nodeId) {
      await updateCloneCard(treeId, nodeId, null)
    }
  } catch (err) {
    console.log(err)
  }
}

/**
 * deleteNode:
 *   Node の内容を削除する
 *   内容文のみをDBから抹消し marker を 'deleted' にする
 *
 * @param {*} treeId
 * @param {*} questionId
 */
export async function deleteNode (treeId, questionId) {
  // console.log('deleteNode, questionId: ' + questionId + ', parentId: ' + parentId)

  const nodeData = {
    question: 'このカードは削除されました',
    marker: 'deleted',
    mentionedUsers: [],
    cloneCardId: null,
    reference: null
  }

  await _updateNode(treeId, questionId, nodeData)
}

/**
 * _updateNode:
 *   trees/{treeId}/nodes/{nodeId} を更新する
 *   updatedAt のタイムスタンプは自動で付与する
 * @param {string} treeId
 * @param {string} nodeId
 * @param {object} nodeData
 * @returns
 */
async function _updateNode (treeId, nodeId, nodeData) {
  if (location.hostname !== DOMAIN_NAME) { console.log(`_updateNode(${treeId}, ${nodeId}, ${JSON.stringify(nodeData)})`) }
  if (typeof (nodeData) !== 'object') {
    return null
  }
  nodeData.updatedAt = serverTimestamp()
  const nodeDocRef = doc(db, 'treesV2/' + treeId + '/nodes', nodeId)
  try {
    const nodeRef = await updateDoc(nodeDocRef, nodeData)
    return nodeRef
  } catch (err) {
    throw err
  }
}

/**
 * updateMarker:
 *   node の marker を更新する
 * @param {string} treeId
 * @param {string} nodeId
 * @param {string} marker
 * @returns
 */
export async function updateMarker (treeId, nodeId, marker) {
  if (location.hostname !== DOMAIN_NAME) { console.log(`updateMarker(${treeId}, ${nodeId}, ${marker})`) }
  const nodeData = {
    marker: marker
  }
  try {
    const nodeRef = await _updateNode(treeId, nodeId, nodeData)
    return nodeRef
  } catch (err) {
    throw err
  }
}

/**
 * updateCloneCard:
 *   node の clone情報 を更新する
 * @param {string} treeId
 * @param {string} nodeId
 * @param {string} cloneCardId
 * @returns
 */
export async function updateCloneCard (treeId, nodeId, cardId) {
  const nodeData = {
    cloneCardId: cardId
  }
  try {
    const nodeRef = await _updateNode(treeId, nodeId, nodeData)
    return nodeRef
  } catch (err) {
    throw err
  }
}

/**
 * updateNodeText:
 *   node の text を更新する
 * @param {string} treeId
 * @param {string} nodeId
 * @param {string} text
 * @returns
 */
export async function updateNodeText (treeId, nodeId, question, mentionedUsers, editMentionedUsers) {
  let nodeData = {
    question: question
  }
  // 編集前と後の差分
  // 前後でmention配列が一致しているケース
  if (editMentionedUsers.length === mentionedUsers.length) {
    // 配列の中身の比較
    const diffMentionedUsers = editMentionedUsers.filter(id => mentionedUsers.indexOf(id) === -1)
    // 差分がある場合
    if (diffMentionedUsers.length > 0) {
      // menntionを更新
      nodeData.mentionedUsers = editMentionedUsers
    }
  // 前後のmention配列が一致していないケース
  } else {
    // menntionを更新
    nodeData.mentionedUsers = editMentionedUsers
  }

  try {
    const nodeRef = await _updateNode(treeId, nodeId, nodeData)
    return nodeRef
  } catch (err) {
    throw err
  }
}

/**
 * updateParentId:
 *   node の parentId を更新する
 * @param {string} treeId
 * @param {string} nodeId
 * @param {string} oldParentId
 * @param {string} newParentId
 * @returns
 */
export async function updateParentId (treeId, nodeId, oldParentId, newParentId) {
  // update old parent node's childsNum -1
  const oldParentNodeDocRef = doc(db, 'treesV2/' + treeId + '/nodes', oldParentId)
  try {
    await runTransaction(db, async (transaction) => {
      const parentNode = await transaction.get(oldParentNodeDocRef)
      const childsNum = (parentNode.data().childsNum - 1 > 0) ? parentNode.data().childsNum - 1 : 0
      transaction.update(oldParentNodeDocRef, { childsNum: childsNum })
    })
  } catch (err) {
    console.error(err)
    return null
  }
  // update new parent node's childsNum + 1
  const newParentNodeDocRef = doc(db, 'treesV2/' + treeId + '/nodes', newParentId)
  try {
    await runTransaction(db, async (transaction) => {
      const parentNode = await transaction.get(newParentNodeDocRef)
      const childsNum = parentNode.data().childsNum + 1
      transaction.update(newParentNodeDocRef, { childsNum: childsNum })
    })
  } catch (err) {
    console.error(err)
    return null
  }
  // update target node's parentId
  const nodeData = {
    parentId: newParentId,
    order: Date.now()
  }
  try {
    const nodeRef = await _updateNode(treeId, nodeId, nodeData)
    return nodeRef
  } catch (err) {
    throw err
  }
}

/**
 * updateNodeOrder:
 *   node の order を更新する
 * @param {string} treeId
 * @param {string} nodeId
 * @param {string} order
 * @returns
 */
export async function updateNodeOrder (treeId, nodeId, order) {
  const nodeData = {
    order: order
  }
  try {
    const nodeRef = await _updateNode(treeId, nodeId, nodeData)
    return nodeRef
  } catch (err) {
    throw err
  }
}

/*
 * Likes関係
 */
export async function addLike (treeId, nodeId) {
  const userId = await getMyUserId()
  // add node to db.
  let likesData = {
    createdAt: serverTimestamp()
  }
  try {
    const likesDocRef = doc(db, 'treesV2/' + treeId + '/nodes/' + nodeId + '/likes', userId)
    await setDoc(likesDocRef, likesData)
  } catch (err) {
    throw err
  }
}
export async function removeLike (treeId, nodeId) {
  const userId = await getMyUserId()
  try {
    const likesDocRef = doc(db, 'treesV2/' + treeId + '/nodes/' + nodeId + '/likes', userId)
    await deleteDoc(likesDocRef)
    // Storeのstateから削除
    let userLikeMap = new Map(Store.state.userLikeMap)
    userLikeMap.delete(treeId + nodeId)
    Store.commit('setUserLikeMap', userLikeMap)
  } catch (err) {
    throw err
  }
}
export async function hasLikes (treeId, nodeId) {
  const userId = await getMyUserId()
  if (!userId) { return false }

  let userLikeMap = new Map(Store.state.userLikeMap)
  if (userLikeMap.get(treeId + nodeId)) {
    return true
  }
  try {
    const likesDocRef = doc(db, 'treesV2/' + treeId + '/nodes/' + nodeId + '/likes', userId)
    const likeSnapshot = await getDoc(likesDocRef)
    if (likeSnapshot.exists()) {
      userLikeMap.set(treeId + nodeId, userId)
      Store.commit('setUserLikeMap', userLikeMap)
    }
    return likeSnapshot.exists()
  } catch (err) {
    throw err
  }
}



// WEBPACK FOOTER //
// ./src/api/FirebaseTreesWrapper.js